import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Inbox, Mail, Building2, User, Loader2, RefreshCw, ExternalLink, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'converted', label: 'Converted' },
  { key: 'rejected', label: 'Rejected' },
];

const STATUS_STYLES = {
  new: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  contacted: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  converted: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  rejected: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-MY', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
}

export default function DemoRequestsPage({ user, onLogout }) {
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({ new: 0, contacted: 0, converted: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/marketing/demo-requests`);
      setRequests(data?.requests || []);
      setCounts(prev => data?.counts || prev);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to load demo requests';
      toast.error(typeof detail === 'string' ? detail : 'Failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return requests.filter(r => {
      const statusOk = filter === 'all' || (r.status || 'new') === filter;
      if (!statusOk) return false;
      if (!q) return true;
      return [r.name, r.email, r.company, r.role, r.message]
        .filter(Boolean)
        .some(v => v.toLowerCase().includes(q));
    });
  }, [requests, filter, query]);

  const updateStatus = async (id, status, addNotes = '') => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/marketing/demo-requests/${id}`, {
        status, notes: addNotes,
      });
      // Update in-place
      setRequests(rs => rs.map(r => r.id === id ? data.request : r));
      setSelected(data.request);
      toast.success(`Marked as ${status}`);
      // Refresh counts (cheap re-fetch)
      load();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to update';
      toast.error(typeof detail === 'string' ? detail : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = (r) => {
    setSelected(r);
    setNotes(r.notes || '');
  };

  return (
    <Layout user={user} onLogout={onLogout} activePage="demo-requests">
      <div className="space-y-6" data-testid="demo-requests-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
              <Inbox className="w-6 h-6" /> Demo Requests
            </h1>
            <p className="text-sm text-slate-500 mt-1">Inbound leads from the landing page. Move them through your pipeline.</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { key: 'total', label: 'Total', color: 'text-slate-100' },
            { key: 'new', label: 'New', color: 'text-sky-300' },
            { key: 'contacted', label: 'Contacted', color: 'text-amber-300' },
            { key: 'converted', label: 'Converted', color: 'text-emerald-300' },
            { key: 'rejected', label: 'Rejected', color: 'text-rose-300' },
          ].map(s => (
            <Card key={s.key} data-testid={`stat-${s.key}`}>
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">{s.label}</div>
                <div className={`mt-1 text-2xl font-bold tabular-nums ${s.color}`}>{counts[s.key] ?? 0}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-1 rounded-lg border p-1 bg-background">
            {STATUSES.map(s => (
              <button
                key={s.key}
                onClick={() => setFilter(s.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${filter === s.key ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-900'}`}
                data-testid={`filter-${s.key}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input
              placeholder="Search name, email, company…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* List */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm text-slate-500 font-medium">{filtered.length} request{filtered.length !== 1 ? 's' : ''}</CardTitle></CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center text-slate-500 text-sm">
                {requests.length === 0 ? 'No demo requests yet — share your landing page!' : 'No requests match your filters.'}
              </div>
            ) : (
              <ul className="divide-y">
                {filtered.map(r => (
                  <li
                    key={r.id}
                    onClick={() => openDetail(r)}
                    className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    data-testid={`request-row-${r.id}`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900">{r.name}</span>
                          <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${STATUS_STYLES[r.status || 'new']}`} data-testid={`status-badge-${r.id}`}>
                            {r.status || 'new'}
                          </Badge>
                        </div>
                        <div className="mt-1 flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                          <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {r.company}</span>
                          <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {r.email}</span>
                          {r.role && <span className="flex items-center gap-1"><User className="w-3 h-3" /> {r.role}</span>}
                        </div>
                        {r.message && (
                          <p className="mt-2 text-sm text-slate-600 line-clamp-2">{r.message}</p>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 text-right shrink-0">
                        {fmtDate(r.submitted_at)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selected.name}
                  <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${STATUS_STYLES[selected.status || 'new']}`}>
                    {selected.status || 'new'}
                  </Badge>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-3 py-2 text-sm">
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-slate-500 text-xs uppercase tracking-wider">Company</div>
                  <div className="col-span-2 font-medium">{selected.company}</div>
                  <div className="text-slate-500 text-xs uppercase tracking-wider">Email</div>
                  <div className="col-span-2">
                    <a href={`mailto:${selected.email}`} className="text-sky-600 hover:underline inline-flex items-center gap-1">
                      {selected.email} <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="text-slate-500 text-xs uppercase tracking-wider">Role</div>
                  <div className="col-span-2">{selected.role || '—'}</div>
                  <div className="text-slate-500 text-xs uppercase tracking-wider">Submitted</div>
                  <div className="col-span-2">{fmtDate(selected.submitted_at)}</div>
                  {selected.updated_at && (
                    <>
                      <div className="text-slate-500 text-xs uppercase tracking-wider">Updated</div>
                      <div className="col-span-2">{fmtDate(selected.updated_at)} {selected.updated_by ? `by ${selected.updated_by}` : ''}</div>
                    </>
                  )}
                </div>

                {selected.message && (
                  <div className="pt-2">
                    <div className="text-slate-500 text-xs uppercase tracking-wider mb-1">Message</div>
                    <p className="text-slate-700 bg-slate-50 border rounded-md p-3 whitespace-pre-wrap">{selected.message}</p>
                  </div>
                )}

                <div className="pt-2">
                  <div className="text-slate-500 text-xs uppercase tracking-wider mb-1">Internal notes</div>
                  <Textarea
                    rows={3}
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Add context — call outcome, next step…"
                    className="resize-none"
                    data-testid="notes-input"
                  />
                </div>
              </div>

              <DialogFooter className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={saving}
                  onClick={() => updateStatus(selected.id, 'contacted', notes)}
                  data-testid="mark-contacted-btn"
                >
                  Mark contacted
                </Button>
                <Button
                  size="sm"
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => updateStatus(selected.id, 'converted', notes)}
                  data-testid="mark-converted-btn"
                >
                  Mark converted
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={saving}
                  className="border-rose-200 text-rose-600 hover:bg-rose-50"
                  onClick={() => updateStatus(selected.id, 'rejected', notes)}
                  data-testid="mark-rejected-btn"
                >
                  Reject
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
